import { classifyLive, type Device, type LiveSnapshot, type LiveState, type LiveView, type Site, type Vehicle } from "@fts/core";

/** One row of the live map: a vehicle with its device, latest state and classification. */
export type LiveVehicle = {
  vehicle: Vehicle;
  device: Device | null;
  snapshot: LiveSnapshot | null;
  view: LiveView;
};

/** Filter chips on the live map. "alarm" cuts across the motion states. */
export const LIVE_FILTERS = ["all", "working", "idle", "off", "offline", "alarm"] as const;
export type LiveFilter = (typeof LIVE_FILTERS)[number];

const NO_DATA: LiveView = { state: "offline", alarm: false, hasPosition: false, positionStale: false };

/**
 * Join vehicles, devices and latest states. Only active vehicles with a paired device are tracked;
 * a snapshot counts only while it belongs to the vehicle's CURRENT device (a moved device's old row is ignored).
 */
export function buildLiveVehicles(vehicles: Vehicle[], devices: Device[], snapshots: Map<string, LiveSnapshot>, now: Date): LiveVehicle[] {
  return vehicles
    .filter((v) => v.active && v.deviceId)
    .map((vehicle) => {
      const device = devices.find((d) => d.deviceId === vehicle.deviceId) ?? null;
      const s = snapshots.get(vehicle.deviceId!);
      const snapshot = s && s.vehicleId === vehicle.id ? s : null;
      // The device list (online flags) refreshes every minute; a live message that arrived after the
      // device's last-seen time proves it is online (ingest sets online on every message).
      const heard = snapshot ? Date.parse(snapshot.receivedAt ?? snapshot.ts) : Number.NaN;
      const seen = device?.lastSeenAt ? Date.parse(device.lastSeenAt) : Number.NEGATIVE_INFINITY;
      const online = (device?.online ?? false) || (device !== null && !device.revoked && Number.isFinite(heard) && heard > seen);
      const view = snapshot ? classifyLive(snapshot, online, now) : NO_DATA;
      return { vehicle, device, snapshot, view };
    })
    .sort((a, b) => a.vehicle.fleetNo.localeCompare(b.vehicle.fleetNo, undefined, { numeric: true }));
}

export function matchesFilter(lv: LiveVehicle, f: LiveFilter): boolean {
  if (f === "all") return true;
  if (f === "alarm") return lv.view.alarm;
  return lv.view.state === f;
}

export function countByFilter(list: LiveVehicle[]): Record<LiveFilter, number> {
  const c = { all: 0, working: 0, idle: 0, off: 0, offline: 0, alarm: 0 } as Record<LiveFilter, number>;
  for (const lv of list) for (const f of LIVE_FILTERS) if (matchesFilter(lv, f)) c[f] += 1;
  return c;
}

const time = (iso: string | null): number => (iso ? Date.parse(iso) : Number.NEGATIVE_INFINITY);

/**
 * Keep the newest version of each device's row. Realtime and full reloads can arrive out of order;
 * timestamps are compared as instants (PostgREST and Realtime may format them differently).
 * A row wins if it belongs to another vehicle (re-pairing), its telemetry time is not older, or it
 * carries a newer event or alarm (event updates do not move `ts`).
 */
export function isNewer(next: LiveSnapshot, prev: LiveSnapshot | undefined): boolean {
  if (!prev || prev.vehicleId !== next.vehicleId) return true;
  return time(next.ts) >= time(prev.ts) || time(next.lastEventAt) > time(prev.lastEventAt) || time(next.lastAlarmAt) > time(prev.lastAlarmAt);
}

export function mergeSnapshot(map: Map<string, LiveSnapshot>, s: LiveSnapshot): Map<string, LiveSnapshot> {
  if (!isNewer(s, map.get(s.deviceId))) return map;
  const next = new Map(map);
  next.set(s.deviceId, s);
  return next;
}

/**
 * Apply a full reload: devices missing from it are dropped (left the tenant or unpaired), but a row
 * that Realtime delivered while the request was in flight is not replaced by an older copy.
 */
export function mergeFullLoad(map: Map<string, LiveSnapshot>, rows: LiveSnapshot[]): Map<string, LiveSnapshot> {
  const next = new Map<string, LiveSnapshot>();
  for (const r of rows) {
    const prev = map.get(r.deviceId);
    next.set(r.deviceId, prev && !isNewer(r, prev) ? prev : r);
  }
  return next;
}

/** Sites with coordinates, for site pins on the map. */
export function placedSites(sites: Site[]): Site[] {
  return sites.filter((s) => s.lat !== null && s.lon !== null);
}

/** Token suffix for state colours (CSS classes live-<state>). */
export const STATE_ORDER: LiveState[] = ["working", "idle", "off", "offline"];
